import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_BASE_URL from '../config';

function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

// Async thunk for fetching all projects
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_BASE_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Extract projects array and totalCount from response
      return {
        projects: response.data.projects || [],
        totalCount: response.data.totalCount || 0
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch projects');
    }
  }
);

// Async thunk for fetching a single project by ID
export const fetchProjectById = createAsyncThunk(
  'projects/fetchProjectById',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_BASE_URL}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Extract project object from response
      if (response.data.success && response.data.project) {
        return response.data.project;
      }
      
      // Handle case where success is true but no project
      return rejectWithValue('Project not found');
    } catch (error) {
      // Handle 404 and other errors
      if (error.response?.status === 404) {
        const errorMsg = error.response.data?.message || error.response.data?.error || 'Project not found';
        return rejectWithValue(errorMsg);
      }
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch project';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for creating a new project
export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_BASE_URL}/api/projects`, projectData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Return the created project (API likely returns the project object)
      return response.data.project || response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to create project';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for updating a project
export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, projectData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.put(`${API_BASE_URL}/api/projects/${id}`, projectData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Return the updated project
      return response.data.project || response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        const errorMsg = error.response.data?.message || error.response.data?.error || 'Project not found';
        return rejectWithValue(errorMsg);
      }
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to update project';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for deleting a project
export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_BASE_URL}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Return the project ID for removal from state
      return projectId;
    } catch (error) {
      if (error.response?.status === 404) {
        const errorMsg = error.response.data?.message || error.response.data?.error || 'Project not found';
        return rejectWithValue(errorMsg);
      }
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to delete project';
      return rejectWithValue(errorMsg);
    }
  }
);

const initialState = {
  allProjects: [],
  currentProject: null,
  totalCount: 0,
  loading: false,
  error: null,
  success: false,
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
    },
    clearCurrentProject: (state) => {
      state.currentProject = null;
    },
    /** Call on logout / login so the next user never sees another user's project list */
    resetProjectsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.allProjects = action.payload.projects;
        state.totalCount = action.payload.totalCount;
        state.error = null;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch project by ID
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = action.payload;
        state.error = null;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.allProjects.push(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update project
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.allProjects.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.allProjects[index] = action.payload;
        }
        if (state.currentProject?.id === action.payload.id) {
          state.currentProject = action.payload;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete project
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.allProjects = state.allProjects.filter((p) => p.id !== action.payload);
        if (state.currentProject?.id === action.payload) {
          state.currentProject = null;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearError, clearSuccess, setCurrentProject, clearCurrentProject, resetProjectsState } =
  projectsSlice.actions;

export default projectsSlice.reducer;
